import React, {
	forwardRef,
	lazy,
	Suspense,
	useContext,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import appConfig from "config.json";

import { Flyout, SectionTitle } from "@wdpr/groot-components";
import { Context } from "Context";
import useKeyboardNav from "hooks/useKeyboardNav";
import LinkGroup from "./LinkGroup";

const mainClass = "syndicated-section";
const { sectionsConfiguration, brands } = appConfig;

const FlyoutPrimary = lazy(() =>
	import("@wdpr/groot-components").then((m) => ({ default: m.Flyout.Primary }))
);
const FlyoutSecondary = lazy(() =>
	import("@wdpr/groot-components").then((m) => ({ default: m.Flyout.Generic }))
);

const getClasses = ({
	mainClass,
	id,
	brand,
	isMobileView,
	collapse,
	showFlyout,
	isMenuActive,
	onFocus,
	store,
	isDecorative,
	showInMobile,
	onHover,
}) => {
	let classes = mainClass;
	if (id) classes += ` ${mainClass}--${id}`;
	if (brand) classes += ` ${mainClass}--${brand}`;
	if (isMobileView && collapse) classes += ` ${mainClass}--open`;
	if (!showFlyout) classes += ` ${mainClass}--no-flyout`;
	if (isMenuActive && onFocus) classes += ` ${mainClass}--onfocus`;
	if (store) classes += ` ${mainClass}--store`;
	if (isDecorative) classes += ` ${mainClass}--decorative`;
	if (showInMobile && isDecorative) classes += ` ${mainClass}--show-in-mobile`;
	if (onHover) classes += ` ${mainClass}--onhover`;

	return classes;
};

const Section = forwardRef(
	(
		{
			id,
			title,
			icon,
			link,
			primary,
			secondary,
			showFlyout = true,
			collapse,
			accessibilityText,
			setCollapsedSection,
			isMenuActive,
			setIsMenuActive,
			store,
			brand,
			flyoutPosition,
			flyoutColumn,
			isLastSection,
			decorativeSection,
			showInMobile,
			reduceLayout,
			showSecondary,
			image,
			handleFullClose,
		},
		ref
	) => {
		const { isMobileView, cdnUrl, cdnAssetsUrl } = useContext(Context);
		const sectionRef = useRef();
		useImperativeHandle(ref, () => sectionRef.current);

		const [onHover, setOnHover] = useState(false);
		const { onFocus, handleKeyDown, handleOnFocus, handleOnBlur } =
			useKeyboardNav(sectionRef, isMenuActive, setIsMenuActive, isLastSection);

		const isDecorative = decorativeSection === "true";
		const isColumn = flyoutColumn === "true";

		const currentBrandConfig = brands[brand] || {};
		const duplicateSectionLinkOnMobile =
			currentBrandConfig.duplicateSectionLinkOnMobile || false;
		const handleCollapse = (e) => {
			if (isMobileView) {
				e?.preventDefault();
				setCollapsedSection((currentCollapsedSection) =>
					currentCollapsedSection === id ? "" : id
				);
			}
		};
		const handleMouseEnter = () => {
			setOnHover(true);
		};
		const handleMouseLeave = () => {
			setOnHover(false);
		};

		if (!title) return null;

		return (
			<div
				className={getClasses({
					mainClass,
					id,
					brand,
					isMobileView,
					collapse,
					showFlyout,
					isMenuActive,
					onFocus,
					store,
					isDecorative,
					showInMobile,
					onHover,
				})}
				tabIndex={isMobileView ? null : -1}
				ref={sectionRef}
				onKeyDown={handleKeyDown}
				onFocus={handleOnFocus}
				onBlur={handleOnBlur}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				role="group"
			>
				<SectionTitle
					collapse={isMobileView ? collapse : onHover || isMenuActive}
					link={{
						...link, //
						handleClick: showFlyout ? handleCollapse : null,
					}}
					icon={icon}
					title={title}
					name={id}
					isMobileView={isMobileView}
					accessibilityText={accessibilityText}
					brand={brand}
					showFlyout={showFlyout}
					cdnUrl={cdnUrl}
					cdnAssetsUrl={cdnAssetsUrl}
				/>
				{showFlyout && (
					<Suspense fallback={null}>
						<Flyout
							collapse={collapse}
							left={
								flyoutPosition
									? flyoutPosition === "left"
									: sectionsConfiguration.flyoutPosition.left.includes(id)
							}
							column={sectionsConfiguration.column.includes(id) || isColumn}
							name={id}
							store={store}
						>
							{primary && (
								<Suspense fallback={null}>
									<FlyoutPrimary
										name={id}
										isMobileView={isMobileView}
										image={image}
									>
										<div
											className={`${mainClass}__content ${mainClass}__content--${id}`}
										>
											{isMobileView && showSecondary && (
												<LinkGroup
													{...secondary}
													duplicateSectionLinkOnMobile={
														duplicateSectionLinkOnMobile
													}
													link={link}
													id={id}
													mainClass={`${mainClass}__content--${id}__primary`}
													isMobileView={isMobileView}
													handleFullClose={handleFullClose}
												/>
											)}
											<LinkGroup
												{...primary}
												duplicateSectionLinkOnMobile={
													duplicateSectionLinkOnMobile
												}
												link={link}
												id={id}
												mainClass={`${mainClass}__content--${id}__primary`}
												isMobileView={isMobileView}
												handleFullClose={handleFullClose}
											/>
										</div>
									</FlyoutPrimary>
								</Suspense>
							)}
							{(secondary?.descriptions?.length > 0 ||
								secondary?.modules?.length > 0) && (
								<Suspense fallback={null}>
									<FlyoutSecondary
										name={id}
										background={
											appConfig.brands[brand]?.secondaryFlyoutBackground ||
											"blue"
										}
										brand={brand}
										promosContainer={secondary.promosContainer}
										isDecorative={isDecorative}
										showInMobile={showInMobile}
										reduceLayout={reduceLayout}
									>
										<div
											className={`${mainClass}__content ${mainClass}__content--${id}`}
										>
											<LinkGroup
												{...secondary}
												id={id}
												mainClass={`${mainClass}__content--${id}__secondary`}
												isMobileView={isMobileView}
												isDecorative={isDecorative}
												secondary
												showInMobile={showInMobile}
												reduceLayout={reduceLayout}
												handleFullClose={handleFullClose}
											/>
										</div>
									</FlyoutSecondary>
								</Suspense>
							)}
						</Flyout>
					</Suspense>
				)}
			</div>
		);
	}
);

export default Section;
